import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { Suspense, lazy } from 'react';

// Components and contexts
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';
import GoogleAnalytics from './components/Analytics';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';
import { SEOProvider } from './contexts/SEOContext';
import { MainLayout } from './layouts';
import { getApiUrl } from './data/api';

// Critical pages (loaded immediately)
import Home from './pages/Home';
import Products from './pages/Products';
import Cart from './pages/Cart';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import Register from './pages/Register';
import Disclaimer from './pages/Disclaimer';
import ClothingGuide from './pages/ClothingGuide';
import TempMaintenance from './pages/TempMaintenance';
import Sitemap from './pages/Sitemap';

// Heavy pages (lazy loaded for better performance)
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const OrderDetails = lazy(() => import('./pages/OrderDetails'));

// Admin pages (lazy loaded - contains Recharts)
const Admin = lazy(() => import('./pages/Admin'));
const AdminProducts = lazy(() => import('./pages/AdminProducts'));
const AdminThemes = lazy(() => import('./pages/AdminThemes'));
const AdminWebhooks = lazy(() => import('./pages/AdminWebhooks'));
const AdminErrors = lazy(() => import('./pages/AdminErrors'));
const AdminOrders = lazy(() => import('./pages/AdminOrders'));
const AdminOrderDetails = lazy(() => import('./pages/AdminOrderDetails'));
const AdminTaxReports = lazy(() => import('./pages/AdminTaxReports'));

// Auth pages (lazy loaded)
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));

// Log the API URL for information purposes
console.debug('API URL:', getApiUrl());

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="50vh"
    role="status"
    aria-label="Loading page content"
  >
    <CircularProgress size={40} />
  </Box>
);

function App() {
  // TEMPORARY: Maintenance mode while fixing email issues
  const showMaintenance = false; // Set to false when emails are fixed


  return (
    <SEOProvider>
      <ThemeProvider>
        <AccessibilityProvider>
          <AuthProvider>
            <CartProvider>
              <ToastProvider>
                <Router>
                <CssBaseline />
                <GoogleAnalytics />
                <ScrollToTop />
                
                {showMaintenance ? (
                  // Temporary maintenance mode
                  <Routes>
                    <Route path="*" element={<TempMaintenance />} />
                  </Routes>
                ) : (
                  // Normal site routes
                  <Routes>
                    {/* Main Layout Routes */}
                    <Route path="/" element={<MainLayout><Home /></MainLayout>} />
                    <Route path="/products" element={<MainLayout><Products /></MainLayout>} />
                    <Route path="/products/:id" element={
                      <MainLayout>
                        <Suspense fallback={<PageLoader />}>
                          <ProductDetail />
                        </Suspense>
                      </MainLayout>
                    } />
                    <Route path="/cart" element={<MainLayout><Cart /></MainLayout>} />
                    <Route path="/about" element={<MainLayout><About /></MainLayout>} />
                    <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
                    <Route path="/disclaimer" element={<MainLayout><Disclaimer /></MainLayout>} />
                    <Route path="/clothing-guide" element={<MainLayout><ClothingGuide /></MainLayout>} />
                    <Route path="/sitemap" element={<MainLayout><Sitemap /></MainLayout>} />
                    
                    {/* Auth Routes (with navbar/footer) */}
                    <Route path="/login" element={<MainLayout><Login /></MainLayout>} />
                    <Route path="/signup" element={<MainLayout><Register /></MainLayout>} />
                    <Route path="/forgot-password" element={
                      <MainLayout>
                        <Suspense fallback={<PageLoader />}>
                          <ForgotPassword />
                        </Suspense>
                      </MainLayout>
                    } />
                    <Route path="/reset-password" element={
                      <MainLayout>
                        <Suspense fallback={<PageLoader />}>
                          <ResetPassword />
                        </Suspense>
                      </MainLayout>
                    } />
                    <Route path="/verify-email" element={
                      <MainLayout>
                        <Suspense fallback={<PageLoader />}>
                          <VerifyEmail />
                        </Suspense>
                      </MainLayout>
                    } />
                    
                    {/* Main Layout Routes for authenticated users */}
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Suspense fallback={<PageLoader />}>
                            <Profile />
                          </Suspense>
                        </MainLayout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Checkout Routes */}
                    <Route path="/checkout" element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Suspense fallback={<PageLoader />}>
                            <Checkout />
                          </Suspense>
                        </MainLayout>
                      </ProtectedRoute>
                    } />
                    <Route path="/order/confirmation/:orderId" element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Suspense fallback={<PageLoader />}>
                            <OrderConfirmation />
                          </Suspense>
                        </MainLayout>
                      </ProtectedRoute>
                    } />
                    <Route path="/order/:orderId" element={
                      <ProtectedRoute>
                        <MainLayout>
                          <Suspense fallback={<PageLoader />}>
                            <OrderDetails />
                          </Suspense>
                        </MainLayout>
                      </ProtectedRoute>
                    } />
                    
                    {/* Admin Routes (AdminLayout includes MainLayout internally) */}
                    <Route path="/admin" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <Admin />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/products" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminProducts />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/orders" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminOrders />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/orders/:orderId" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminOrderDetails />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/themes" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminThemes />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/webhooks" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminWebhooks />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/errors" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminErrors />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                    <Route path="/admin/tax-reports" element={
                      <ProtectedRoute requireAdmin={true}>
                        <Suspense fallback={<PageLoader />}>
                          <AdminTaxReports />
                        </Suspense>
                      </ProtectedRoute>
                    } />
                  </Routes>
                )}
                </Router>
              </ToastProvider>
            </CartProvider>
          </AuthProvider>
        </AccessibilityProvider>
      </ThemeProvider>
    </SEOProvider>
  );
}

export default App;
